import api, { apiErrorHandler } from "api";
import { ScheduleConstants, SUPERADMIN } from "constants/constants";
import { customToast } from "hooks/toast";
import { RootState } from "reducers/rootReducer";
import { Dispatch } from "redux";
import { ScheduleSelectItem, ScheduleReport } from "types/Schedule";
import { getTimeAfterPost } from "utils/filters";
import {
  getFilteredScheduleIds,
  getScheduleReportForUserDate,
  isScheduleSelected,
  repeatingDispOrVisor,
} from "utils/scheduleHelpers";

export const getScheduleData =
  (params: { station?: string; year_number?: number; week?: number, statuses: string, }) => (dispatch: Dispatch) => {
    const onError = () => dispatch({ type: ScheduleConstants.SCHEDULE_TABLE_GET_DATA_ERROR });
    dispatch({ type: ScheduleConstants.SCHEDULE_TABLE_GET_DATA_LOADING });
    api
      .getScheduleData(params)
      .then((res) => {
        if (res?.data) {
          dispatch({
            type: ScheduleConstants.SCHEDULE_TABLE_GET_DATA_SUCCESS,
            payload: {
              dateList: res?.data.date_list,
              planList: res?.data.plan_list,
              dataList: res?.data.data_list,
            },
          });
        }
      })
      .catch(apiErrorHandler(onError));
  };

export const getUsersData =
  (search: string, station: number, statuses: string) =>
  async (dispatch: Dispatch, getStore: () => RootState) => {
    const onError = () => dispatch({ type: ScheduleConstants.SCHEDULE_TABLE_GET_DATA_ERROR });
    dispatch({ type: ScheduleConstants.SCHEDULE_TABLE_GET_DATA_LOADING });

    api
      .getCustomUsersList({
        page_size: 999,
        search: search ? `${search}` : "",
        ordering: "-role",
        in_station: station,
        is_schedule_users: true,
        schedule_statuses: statuses,
      })
      .then((res) => {
        if (res?.data) {
          dispatch({
            type: ScheduleConstants.SCHEDULE_GET_ALL_USERS_SUCCESS,
            payload: res?.data.results,
          });
        }
      })
      .catch(apiErrorHandler(onError));
  };

export const scheduleCreateUpdateReport =
  (
    date: string | undefined,
    user_id: number,
    helper_of: number | null,
    role: number | null,
    role_hours: number | null,
    is_available: boolean,
    shift_start_time: string | null,
  ) =>
  (dispatch: Dispatch, getStore: () => RootState) => {
    const onError = () => dispatch({ type: ScheduleConstants.SCHEDULE_TABLE_GET_DATA_ERROR });
    dispatch({ type: ScheduleConstants.SCHEDULE_TABLE_GET_DATA_LOADING });

    const { dataList, scheduleFilters } = getStore()?.scheduleTable;

    api
      .createUpdateScheduleReport({
        date,
        user_id,
        helper_of,
        role,
        role_hours,
        is_available,
        shift_start_time,
        station: scheduleFilters?.station,
      })
      .then((res) => {
        dispatch({
          type: ScheduleConstants.SCHEDULE_UPDATE_SCHEDULE_REPORT,
          payload:
            res.data.created ||
            (!res.data.created && !dataList.some((item) => item.id === res.data.data.id))
              ? [res.data.data, ...dataList]
              : dataList.map((item: ScheduleReport) =>
                  item?.schedule_date === res.data.data.schedule_date &&
                  item?.assigned_to === res.data.data.assigned_to
                    ? { ...res.data.data }
                    : item,
                ),
        });
      })
      .catch(apiErrorHandler(onError));
  };

export const duplicateCopiedScheduleReport =
  () => (dispatch: Dispatch, getStore: () => RootState) => {
    const onError = () => dispatch({ type: ScheduleConstants.SCHEDULE_TABLE_GET_DATA_ERROR });

    dispatch({ type: ScheduleConstants.SCHEDULE_TABLE_GET_DATA_LOADING });

    const { dataList, scheduleFilters, reportCopy, idListForCopy } = getStore()?.scheduleTable;
    api
      .createUpdateScheduleReport({
        copy_list: JSON.stringify(idListForCopy),
        helper_of: reportCopy?.helper_of,
        station: scheduleFilters?.station,
        role: reportCopy?.role,
        role_hours: reportCopy?.role_hours,
        is_available: reportCopy?.is_available,
        shift_start_time: reportCopy?.shift_start_time,
      })
      .then(() => {
        const copiedFields = {
          helper_of: reportCopy?.helper_of ?? null,
          station: reportCopy?.station,
          station__title: reportCopy?.station__title,
          role: reportCopy?.role,
          role_hours: reportCopy?.role_hours ?? null,
          is_available: reportCopy?.is_available,
          is_published: false,
          shift_start_time: reportCopy?.shift_start_time,
        };

        const updatedList = dataList.map((item: ScheduleReport) =>
          isScheduleSelected(idListForCopy, item?.schedule_date, item?.assigned_to)
            ? { ...item, ...copiedFields }
            : item,
        );

        const newRecords = idListForCopy
          .filter(
            (copyItem) =>
              !updatedList.some(
                (item) =>
                  item.schedule_date === copyItem.date && item.assigned_to === copyItem.user_id,
              ),
          )
          .map((copyItem, index) => ({
            ...copiedFields,
            id: Date.now() + index,
            assigned_to: copyItem.user_id,
            schedule_date: copyItem.date,
            week_number: reportCopy?.week_number ?? 0,
          }));

        dispatch({
          type: ScheduleConstants.SCHEDULE_COPY_SCHEDULE_SUCCESS,
          payload: [...newRecords, ...updatedList],
        });
      })
      .catch(apiErrorHandler(onError));
  };

export const deleteScheduleReportAction =
  (id: number | undefined, date: string | undefined, userId: number | null | undefined) =>
  async (dispatch: Dispatch, getStore: () => RootState) => {
    const { dataList, scheduleFilters } = getStore()?.scheduleTable;
    const onError = () => dispatch({ type: ScheduleConstants.SCHEDULE_TABLE_GET_DATA_ERROR });
    dispatch({ type: ScheduleConstants.SCHEDULE_TABLE_GET_DATA_LOADING });

    api
      .deleteScheduleReport({
        date,
        user_id: userId,
        station: scheduleFilters?.station,
      })
      .then((res) => {
        const newList = dataList
          .map((el) =>
            el.id === id
              ? { ...el, role: null, role_hours: 0, station: null, helper_of: null }
              : el,
          )
          .map((el) => (el.helper_of === userId ? { ...el, helper_of: null } : el));
        dispatch({
          type: ScheduleConstants.SCHEDULE_UPDATE_SCHEDULE_REPORT,
          payload: newList,
        });
      })
      .catch(apiErrorHandler(onError));
  };

export const deleteMultipleScheduleReportAction =
  () => async (dispatch: Dispatch, getStore: () => RootState) => {
    const { dataList, idListForDelete } = getStore()?.scheduleTable;
    const onError = () => dispatch({ type: ScheduleConstants.SCHEDULE_TABLE_GET_DATA_ERROR });
    dispatch({ type: ScheduleConstants.SCHEDULE_TABLE_GET_DATA_LOADING });

    api
      .deleteScheduleReport({
        id_list: JSON.stringify(idListForDelete),
      })
      .then(() => {
        const deletedReports = dataList.filter((el) =>
          isScheduleSelected(idListForDelete, el.schedule_date, el.assigned_to),
        );
        const newList = dataList.map((el) => {
          if (isScheduleSelected(idListForDelete, el.schedule_date, el.assigned_to)) {
            return { ...el, role: null, role_hours: 0, station: null, helper_of: null };
          }

          const isHelperOfDeleted = deletedReports.some(
            (deleted) =>
              deleted.assigned_to != null &&
              el.helper_of === deleted.assigned_to &&
              el.schedule_date === deleted.schedule_date,
          );

          return isHelperOfDeleted ? { ...el, helper_of: null } : el;
        });
        dispatch({
          type: ScheduleConstants.SCHEDULE_MULPIPLE_DELETE_SCHEDULE_REPORT,
          payload: newList,
        });
      })
      .catch(apiErrorHandler(onError));
  };

export const selectAllUsersList = () => (dispatch: Dispatch, getStore: () => RootState) => {
  const { userList, idListForSMS } = getStore()?.scheduleTable;

  dispatch({
    type: ScheduleConstants.SCHEDULE_UPDATE_USER_ID_LIST,
    payload:
      idListForSMS.length >= userList.filter((item) => item.role !== SUPERADMIN).length
        ? []
        : [...userList.filter((item) => item.role !== SUPERADMIN).map((item) => item.id)],
  });
};

export const addOrRemoveIdFromSMSList =
  (id: number) => (dispatch: Dispatch, getStore: () => RootState) => {
    const { idListForSMS } = getStore()?.scheduleTable;

    dispatch({
      type: ScheduleConstants.SCHEDULE_UPDATE_USER_ID_LIST,
      payload: !idListForSMS.includes(id)
        ? [...idListForSMS, id]
        : [
            ...idListForSMS.filter((item: number) => {
              return item !== id;
            }),
          ],
    });
  };

export const addScheduleToCopyListAction =
  (date?: string, userId?: number) => (dispatch: Dispatch, getStore: () => RootState) => {
    if (!date || userId == null) return;

    const { idListForCopy } = getStore()?.scheduleTable;

    dispatch({
      type: ScheduleConstants.SCHEDULE_UPDATE_COPY_ID_LIST,
      payload: !isScheduleSelected(idListForCopy, date, userId)
        ? [...idListForCopy, { date, user_id: userId }]
        : idListForCopy.filter((item) => !(item.date === date && item.user_id === userId)),
    });
  };

export const addAllAvailableScheduleToCopyListAction =
  () => (dispatch: Dispatch, getStore: () => RootState) => {
    const { idListForCopy, dataList, userList, dateList, scheduleFilters, reportCopy } =
      getStore()?.scheduleTable;
    const currentUserRole = getStore()?.auth.userData.role;
    const { userRoles, shiftRoles, search, ordering, station } = scheduleFilters;
    const pastDays = currentUserRole === SUPERADMIN ? -1 : 0;

    const displayedUserIds = getFilteredScheduleIds(
      userList,
      userRoles,
      shiftRoles,
      search,
      ordering || "-id",
      dataList,
      station,
    );
    const displayedDates = dateList.map((item) => item.date).filter(Boolean);

    const availableItems = displayedUserIds.flatMap((userId) =>
      displayedDates.reduce<ScheduleSelectItem[]>((acc, date) => {
        if (getTimeAfterPost(date, pastDays)) return acc;
        if (repeatingDispOrVisor(dataList, date, reportCopy?.role, station)) return acc;

        const report = getScheduleReportForUserDate(dataList, userId, date);
        const isAvailableCell =
          !!report &&
          report.is_available &&
          report.role === null &&
          (!report.station || report.station === Number(station));

        if (!isAvailableCell || isScheduleSelected(idListForCopy, date, userId)) return acc;

        acc.push({ date, user_id: userId });
        return acc;
      }, []),
    );

    dispatch({
      type: ScheduleConstants.SCHEDULE_UPDATE_COPY_ID_LIST,
      payload: [...idListForCopy, ...availableItems],
    });
  };

export const addScheduleToDleteListAction =
  (date?: string, userId?: number) => (dispatch: Dispatch, getStore: () => RootState) => {
    if (!date || userId == null) return;

    const { idListForDelete } = getStore()?.scheduleTable;

    dispatch({
      type: ScheduleConstants.SCHEDULE_UPDATE_DELETE_ID_LIST,
      payload: !isScheduleSelected(idListForDelete, date, userId)
        ? [...idListForDelete, { date, user_id: userId }]
        : idListForDelete.filter((item) => !(item.date === date && item.user_id === userId)),
    });
  };

export const pushRequestScheduleSMSAction =
  (user_id_list: string, week_date: string) => async (dispatch: Dispatch) => {
    api
      .sendRequestSchedule({ user_id_list, week_date })
      .then(() => {
        customToast("success", "Message has been send!");
        dispatch({ type: ScheduleConstants.SCHEDULE_USERS_NOTIFY_SUCCESS });
      })
      .catch(
        apiErrorHandler(() => {
          customToast("error", "Something was wrong!");
          dispatch({ type: ScheduleConstants.SCHEDULE_USERS_NOTIFY_ERROR });
        }),
      );
  };

export const pushConfirmedScheduleSMSAction =
  (station_id: number, week_date: string) => async (dispatch: Dispatch) => {
    api
      .sendConfirmedSchedule({
        user_id_list: "",
        station_id,
        week_date,
      })
      .then(() => {
        customToast("success", "Message has been send!");
        dispatch({ type: ScheduleConstants.SCHEDULE_USERS_NOTIFY_SUCCESS });
      })
      .catch(
        apiErrorHandler(() => {
          customToast("error", "Something was wrong!");
          dispatch({ type: ScheduleConstants.SCHEDULE_USERS_NOTIFY_ERROR });
        }),
      );
  };

export const pushSelectedConfirmedSMSAction =
  (id_list: number[], station_id: number, week_date: string) =>
  async (dispatch: Dispatch, getStore: () => RootState) => {
    api
      .sendConfirmedSchedule({ user_id_list: id_list.join("_"), station_id, week_date })
      .then(() => {
        customToast("success", "Message has been send!");
      })
      .catch(
        apiErrorHandler(() => {
          customToast("error", "Something was wrong!");
          dispatch({ type: ScheduleConstants.SCHEDULE_USERS_NOTIFY_ERROR });
        }),
      );
  };

export const setManualModeAction = () => async (dispatch: Dispatch) => {
  dispatch({
    type: ScheduleConstants.SCHEDULE_SET_MANUAL_MODE,
  });
};

export const setDeleteModeAction = () => async (dispatch: Dispatch) => {
  dispatch({
    type: ScheduleConstants.SCHEDULE_SET_DELETE_MODE,
  });
};

export const setCopyModeAction = (report: ScheduleReport) => async (dispatch: Dispatch) => {
  dispatch({
    type: ScheduleConstants.SCHEDULE_SET_COPY_MODE,
    payload: report,
  });
};

export const scheduleSetFilter =
  (data: any) => async (dispatch: Dispatch, getStore: () => RootState) => {
    const { scheduleFilters } = getStore()?.scheduleTable;

    dispatch({
      type: ScheduleConstants.SCHEDULE_SET_FILTERS,
      payload: {
        ...scheduleFilters,
        ...data,
      },
    });
  };

export const addOrRemoveUserRoleList =
  (id: number) => (dispatch: Dispatch, getStore: () => RootState) => {
    const { scheduleFilters } = getStore()?.scheduleTable;
    const idList = scheduleFilters?.userRoles?.length ? scheduleFilters?.userRoles : [];
    dispatch({
      type: ScheduleConstants.SCHEDULE_SET_FILTERS,
      payload: {
        ...scheduleFilters,
        userRoles: !idList.includes(id)
          ? [...idList, id]
          : [
              ...idList.filter((item: number) => {
                return item !== id;
              }),
            ],
      },
    });
  };

export const addOrRemoveShiftRoleList =
  (id: number) => (dispatch: Dispatch, getStore: () => RootState) => {
    const { scheduleFilters } = getStore()?.scheduleTable;
    const idList = scheduleFilters?.shiftRoles?.length ? scheduleFilters?.shiftRoles : [];
    dispatch({
      type: ScheduleConstants.SCHEDULE_SET_FILTERS,
      payload: {
        ...scheduleFilters,
        shiftRoles: !idList.includes(id)
          ? [...idList, id]
          : [
              ...idList.filter((item: number) => {
                return item !== id;
              }),
            ],
      },
    });
  };
